import * as s from "styles/ModalCommonStyled";
import StepperComponent, { StepType } from "components/blocks/stepper/Stepper";
import BigButton from "components/atoms/buttons/BigButton";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/input/TextArea";
import { UPDATE_TYPE } from "./AddCpContainer";
import {
  CPReport,
  CPReportDataRow,
} from "api/interfaces/reportInterface.interface";
import AddNotification, { NOTIFICATION_TYPE } from "../common/AddNotification";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete";
import { Text, ValidateWord } from "components/atoms/text/Text";
import Toggle from "components/atoms/toggle/Toggle";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { SearchAlertAccount } from "components/element/search/SearchAlertAccount";
import { useMemo } from "react";
import { useAuth } from "components/Auth";

type Props = {
  steps: Array<StepType>;
  page: number;
  selectedAccount: ProfileAccountInfo;
  onChangePage: (name: string, index?: number) => void;
  onChangeHealthReportInfo: (
    type: UPDATE_TYPE,
    value: string | number | boolean | string[] | boolean[] | number[]
  ) => void;
  onChangeNotification: (
    type: NOTIFICATION_TYPE,
    value: string | number | boolean | string[] | boolean[] | number[]
  ) => void;
  hostName: string;
  onClickCreate: () => void;
  data: CPReport;
  isEdit: boolean | undefined;
  isConfirm: boolean;
  setIsDeleteConfirm?: (confirm: boolean) => void;
  onDelete: (value: boolean) => void;
  onConfirmDelete: () => void;
  onChangeEnable: (value: boolean) => void;
  reportRow?: CPReportDataRow;
  reportRuleValidate: Validate;
  reportRuleValidateMsg: ValidateMsg;
  selectedAccountList?: string[];
  saveIsInProgress: boolean;
  deleteIsInProgress: boolean;
};

export default function AddCpPresenter(props: Props): JSX.Element {
  const auth = useAuth();

  const isDefault = props.reportRow?.isDefault;
  const isEditable = useMemo(() => {
    if (!isDefault) {
      return (
        props.selectedAccount.isAdminGroup ||
        auth.user.email === props.reportRow?.email
      );
    }
    return props.selectedAccount.isAdminGroup;
  }, [props.reportRow, props.selectedAccount, auth]);

  return (
    <>
      <StepperComponent
        activeStep={props.page}
        steps={props.steps}
        onClick={props.onChangePage}
        data-testid="reports-add-cp-stepper-component-change-page"
      />

      <s.ModalInnerHalf>
        <div style={{ display: props.page === 0 ? "block" : "none" }}>
          <s.CheckInput>
            <input
              type="checkbox"
              // defaultChecked={props.data.recorder.autoApply}
              checked={props.data.account.autoApply}
              onChange={(e) => {
                props.onChangeHealthReportInfo(
                  UPDATE_TYPE.AUTO_APPLY,
                  e.currentTarget.checked
                );
              }}
              disabled={isDefault}
              data-testid="reports-add-cp-input-auto-apply-account"
            />{" "}
            Apply to all current and future organizations.
          </s.CheckInput>

          {!props.data.account.autoApply && (
            <s.ModalTableContainer>
              <SearchAlertAccount
                accountId={props.selectedAccount.accountId}
                selectedAccountId={props.selectedAccountList}
                onSelectedRowsChange={(value: string[]) => {
                  props.onChangeHealthReportInfo(UPDATE_TYPE.ACCOUNT, value);
                }}
              />
            </s.ModalTableContainer>
          )}

          <ValidateWord data-testid="reports-add-cp-validate-account">
            {props.reportRuleValidateMsg.isRecorder}
          </ValidateWord>
        </div>
        <div style={{ display: props.page === 1 ? "block" : "none" }}>
          <AddNotification
            data={props.data.notification}
            accountId={props.selectedAccount.accountId}
            onChange={props.onChangeNotification}
            isEndOfMonth={props.data.notification.isEndOfMonth}
          />
        </div>
        <div style={{ display: props.page === 2 ? "block" : "none" }}>
          <ModalInput
            label="Title"
            essential
            isValidated={props.reportRuleValidate.isRuleName}
            validatedWord={props.reportRuleValidateMsg.isRuleName}
          >
            <Input
              placeholder="Enter a title for this report"
              value={props.data.preferences.title}
              onChange={(e) =>
                props.onChangeHealthReportInfo(
                  UPDATE_TYPE.REPORT_NAME,
                  e.target.value
                )
              }
              isValidated={props.reportRuleValidate.isRuleName}
              disabled={isDefault}
              maxLength={255}
              data-testid="reports-add-cp-input-change-health-report-info-2"
            />
          </ModalInput>
          <ModalInput label="Host">
            <Input disabled value={props.data.preferences.host} />
          </ModalInput>
          <ModalInput label="Description">
            <TextArea
              placeholder="Please enter a description"
              value={props.data.preferences.description}
              onChange={(e) =>
                props.onChangeHealthReportInfo(UPDATE_TYPE.DESC, e.target.value)
              }
              disabled={isDefault}
              maxLength={255}
              data-testid="reports-add-cp-textarea-change-health-report-info"
            />
          </ModalInput>
          {props.isEdit && (
            <s.RowModalInput>
              <Text bold>Enable</Text>
              <Toggle
                isToggled={
                  props.isEdit
                    ? (props.data.preferences?.isEnable as boolean)
                    : true
                }
                checked={
                  props.isEdit
                    ? (props.data.preferences?.isEnable as boolean)
                    : true
                }
                onChange={(
                  checked: boolean,
                  event: React.MouseEvent<HTMLButtonElement>
                ) => {
                  // e.stopPropagation();
                  props.onChangeEnable(checked);
                }}
                dataTestId="reports-add-cp-enable-toggle"
              />
            </s.RowModalInput>
          )}
        </div>
      </s.ModalInnerHalf>
      <s.ButtonWrapper>
        {props.isEdit && !isDefault && isEditable && (
          <BigButton
            buttonType="warning"
            label="Delete"
            onClickBtn={() =>
              props.setIsDeleteConfirm !== undefined
                ? props.setIsDeleteConfirm(true)
                : null
            }
            disabled={props.deleteIsInProgress}
            data-testid="reports-add-cp-button-set-is-delete-confirm"
          />
        )}

        {props.page !== 0 && (
          <BigButton
            buttonType="cancel"
            label="Previous"
            onClickBtn={() => props.onChangePage("previous")}
            data-testid="reports-add-cp-button-change-page"
          />
        )}

        {props.page === 2 ? (
          isEditable ? (
            <BigButton
              label="Save"
              onClickBtn={props.onClickCreate}
              disabled={props.saveIsInProgress}
              data-testid="reports-add-cp-button-click-create"
            />
          ) : (
            <></>
          )
        ) : (
          <BigButton
            buttonType="cancel"
            label="Next"
            onClickBtn={() => props.onChangePage("next")}
            data-testid="reports-add-cp-button-change-page-2"
          />
        )}

        {props.isConfirm && (
          <DeleteConfirmModal
            label="report rule"
            onDelete={() => props.onDelete(false)}
            onConfirmDelete={props.onConfirmDelete}
          />
        )}
      </s.ButtonWrapper>
    </>
  );
}
